/**
 * Encoding of multi-value list filters, in PostgREST syntax so the Supabase
 * provider passes them through (the FakeRest adapter understands them too):
 * - "in":    status@in=(prospect,client)   one of the values
 * - "array": event_types@ov={a,b}          an array column holding one of them
 * - "json":  custom_fields->k@cs=["a","b"] a JSON array holding all of them
 * - "single": one plain value
 */
export type ListFilterMode = "in" | "array" | "json" | "single";

/** Value of the « À définir » choice: no value at all (is null). */
export const NULL_FILTER_VALUE = "null";

const quote = (value: string) =>
  /[,(){}"\s]/.test(value) ? `"${value.replace(/"/g, "")}"` : value;

const toList = (values: string[], open: string, close: string) =>
  values.length ? `${open}${values.map(quote).join(",")}${close}` : undefined;

/** Values of an "(a,b)" or "{a,b}" filter, quotes removed. */
export const fromListFilter = (value: unknown): string[] => {
  if (typeof value !== "string" || value.length < 3) return [];
  const items: string[] = [];
  let current = "";
  let isQuoted = false;
  for (const char of value.slice(1, -1)) {
    if (char === '"') {
      isQuoted = !isQuoted;
    } else if (char === "," && !isQuoted) {
      items.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  return current ? [...items, current] : items;
};

const fromJsonList = (value: unknown): string[] => {
  if (typeof value !== "string") return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

export const LIST_FILTER_CODECS: Record<
  ListFilterMode,
  {
    read: (value: unknown) => string[];
    write: (values: string[]) => string | undefined;
  }
> = {
  in: { read: fromListFilter, write: (values) => toList(values, "(", ")") },
  array: { read: fromListFilter, write: (values) => toList(values, "{", "}") },
  json: {
    read: fromJsonList,
    write: (values) => (values.length ? JSON.stringify(values) : undefined),
  },
  single: {
    read: (value) => (value == null || value === "" ? [] : [String(value)]),
    write: (values) => values[0],
  },
};

/**
 * Next selection after a click on `value`. A single filter keeps one value;
 * the exclusive value (« À définir ») cannot be combined with the others.
 */
export const toggleFilterValue = (
  selected: string[],
  value: string,
  { isSingle, exclusiveValue }: { isSingle: boolean; exclusiveValue?: string },
): string[] => {
  if (selected.includes(value)) {
    return selected.filter((item) => item !== value);
  }
  if (isSingle || value === exclusiveValue) return [value];
  return [...selected.filter((item) => item !== exclusiveValue), value];
};
